import { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Button,
    Stack,
    Divider,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { URLHandler } from "src/services/URLHandler";
import { MRTGrid } from "@foodpilot/foods";
import { createMRTColumnHelper, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { TagType } from "src/types/tag";
import { useGetAllTagTypes, useDeleteTagType } from "src/api/client-api/tag";
import { useSnackbar } from "src/utils/useSnackbar";
import ActionsPopover from "src/components/ActionsPopover/ActionsPopover";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { GridText } from "src/components/Grid/GridText";
import { FoodsTooltip } from "@foodpilot/foods";

const columnHelper = createMRTColumnHelper<TagType>();

export const TagTypesList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const snackbar = useSnackbar();

    const [paginationModel, setPaginationModel] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "sortOrder", desc: false }]);
    const [search, setSearch] = useState("");
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
    const [deleteDialog, setDeleteDialog] = useState<{
        open: boolean;
        tagType: TagType | null;
    }>({ open: false, tagType: null });

    // Tracks the "type is in use" confirmation step. Opened only when the
    // first delete attempt comes back as 409 with a values + usage count.
    const [inUseDialog, setInUseDialog] = useState<{
        open: boolean;
        tagType: TagType | null;
        valuesCount: number;
        usageCount: number;
    }>({ open: false, tagType: null, valuesCount: 0, usageCount: 0 });

    const {
        tagTypes = [],
        totalItems = 0,
        isLoading,
        refetch,
    } = useGetAllTagTypes({
        page: paginationModel.pageIndex + 1,
        itemsPerPage: paginationModel.pageSize,
        search: search.trim() || undefined,
        includeInactive: true,
        order: sorting.length > 0 ? { [sorting[0].id]: sorting[0].desc ? "desc" : "asc" } : undefined,
    });

    const selectedTagType = tagTypes.find((tt) => tt.id === selectedRowId);

    const deleteTagTypeMutation = useDeleteTagType();

    const columns = [
        columnHelper.accessor("name", {
            header: "Nom",
            Cell: ({ cell }) => <GridText value={cell.getValue()} />,
            size: 250,
        }),
        columnHelper.accessor("context", {
            header: "Contexte",
            Cell: ({ cell }) => <Chip label={cell.getValue()} size="small" sx={{ textTransform: "capitalize" }} />,
            size: 120,
        }),
        columnHelper.accessor("description", {
            header: "Description",
            Cell: ({ cell }) => <GridText value={cell.getValue() || "-"} />,
            size: 300,
        }),
        columnHelper.accessor("color", {
            header: "Couleur",
            Cell: ({ cell }) => (
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: cell.getValue(),
                        border: "1px solid #ccc",
                        borderRadius: 1,
                    }}
                />
            ),
            size: 80,
        }),
        columnHelper.accessor("active", {
            header: "Statut",
            Cell: ({ cell }) => (
                <Chip
                    label={
                        cell.getValue() ? t("common.status.active", "Actif") : t("common.status.inactive", "Inactif")
                    }
                    color={cell.getValue() ? "success" : "default"}
                    size="small"
                />
            ),
            size: 100,
        }),
        columnHelper.accessor("multiple", {
            header: "Multiple",
            Cell: ({ cell }) => (
                <Chip
                    label={cell.getValue() ? t("common.yes", "Oui") : t("common.no", "Non")}
                    color={cell.getValue() ? "primary" : "default"}
                    size="small"
                    variant="outlined"
                />
            ),
            size: 100,
        }),
        columnHelper.accessor("required", {
            header: "Obligatoire",
            Cell: ({ cell }) => (
                <Chip
                    label={cell.getValue() ? t("common.yes", "Oui") : t("common.no", "Non")}
                    color={cell.getValue() ? "warning" : "default"}
                    size="small"
                    variant="outlined"
                />
            ),
            size: 120,
        }),
        columnHelper.accessor("values", {
            header: "Nb Valeurs",
            Cell: ({ cell }) => <GridText value={cell.getValue()?.length?.toString() || "0"} />,
            size: 100,
            enableSorting: false,
        }),
    ];

    // Backend now drives the "is this type still in use?" check and returns
    // a 409 with the values + entities counts. We just open the first-step
    // confirm dialog here; conflicts are escalated to the second-step modal
    // in `handleDelete` below.
    const handleDeleteClick = () => {
        if (!selectedTagType) return;

        setRowMenuAnchor(null);
        setDeleteDialog({ open: true, tagType: selectedTagType });
    };

    const menuItems: MenuItemType[] = [
        {
            icon: <EditIcon />,
            label: t("common.actions.edit", "Modifier"),
            onClick: () => {
                if (selectedRowId) {
                    setRowMenuAnchor(null);
                    navigate(URLHandler.settings.tags.tagTypes.edit(selectedRowId));
                }
            },
        },
        {
            icon: <DeleteIcon />,
            label: t("common.actions.delete", "Supprimer"),
            onClick: handleDeleteClick,
        },
    ];

    // Parse the structured 409 message
    //   `Tag type "X" has N values and is currently used by M entities.`
    // emitted by the backend processor. We extract N (values) and M (entities)
    // independently; either falls back to 0 if the wording shifts so the UI
    // degrades gracefully without lying to the user.
    const extractCounts = (message: unknown): { valuesCount: number; usageCount: number } => {
        if (typeof message !== "string") return { valuesCount: 0, usageCount: 0 };
        const valuesMatch = message.match(/(\d+)\s+values?/i);
        const usageMatch = message.match(/(\d+)\s+entit/i);
        return {
            valuesCount: valuesMatch ? parseInt(valuesMatch[1], 10) : 0,
            usageCount: usageMatch ? parseInt(usageMatch[1], 10) : 0,
        };
    };

    // First-step deletion. Backend returns 409 if the type still has children
    // or is referenced by entities; we escalate to the in-use confirmation
    // dialog instead of just failing with a snackbar.
    const handleDelete = () => {
        if (!deleteDialog.tagType) return;

        const tagType = deleteDialog.tagType;

        deleteTagTypeMutation.mutate(
            { tagTypeId: tagType.id },
            {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        message: t("tags.messages.tag_type_deleted_successfully", "Tag type deleted successfully"),
                        type: "success",
                    });
                    refetch();
                    setDeleteDialog({ open: false, tagType: null });
                },
                onError: (error: unknown) => {
                    const apiError = error as { response?: { status?: number; data?: { detail?: string } } };

                    if (apiError?.response?.status === 409) {
                        setDeleteDialog({ open: false, tagType: null });
                        const { valuesCount, usageCount } = extractCounts(apiError.response.data?.detail);
                        setInUseDialog({ open: true, tagType, valuesCount, usageCount });
                        return;
                    }

                    snackbar.setSnackbarProps({
                        message: t("tags.messages.failed_to_delete_tag_type", "Failed to delete tag type"),
                        type: "error",
                    });
                    setDeleteDialog({ open: false, tagType: null });
                },
            },
        );
    };

    // Second-step deletion triggered from the in-use dialog. Sending force=true
    // tells the backend to strip orphaned references from every taggable entity
    // and cascade-delete child TagValues before removing the type itself.
    const handleForceDelete = () => {
        if (!inUseDialog.tagType) return;

        deleteTagTypeMutation.mutate(
            { tagTypeId: inUseDialog.tagType.id, force: true },
            {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        message: t("tags.messages.tag_type_deleted_successfully", "Tag type deleted successfully"),
                        type: "success",
                    });
                    refetch();
                    setInUseDialog({ open: false, tagType: null, valuesCount: 0, usageCount: 0 });
                },
                onError: () => {
                    snackbar.setSnackbarProps({
                        message: t("tags.messages.failed_to_delete_tag_type", "Failed to delete tag type"),
                        type: "error",
                    });
                    setInUseDialog({ open: false, tagType: null, valuesCount: 0, usageCount: 0 });
                },
            },
        );
    };

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography component="h1" variant="h2">
                    {t("tags.titles.tag_types", "Types de Tags")}
                </Typography>
                <Box>
                    <FoodsTooltip title={t("common.actions.refresh", "Actualiser")}>
                        <IconButton onClick={() => refetch()} size="small" sx={{ marginRight: 1 }}>
                            <RefreshIcon />
                        </IconButton>
                    </FoodsTooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(URLHandler.settings.tags.tagTypes.create())}
                        sx={(theme) => ({
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            textTransform: "none",
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontSize: "0.875rem",
                            fontWeight: 700,
                            lineHeight: "1.5em",
                            boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                            "&:hover": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            },
                        })}
                    >
                        {t("tags.actions.new_type", "New Type")}
                    </Button>
                </Box>
            </Stack>

            <Divider sx={{ mb: 4 }} />

            <Box
                sx={{
                    borderRadius: "16px",
                    border: "1px solid #e0e0e0",
                    background: "#FFF",
                    paddingTop: "32px",
                }}
            >
                <MRTGrid
                    columns={columns}
                    data={tagTypes}
                    enableRowActions={true}
                    enableRowSelection={false}
                    enablePagination={true}
                    enableSelectAll={false}
                    enableColumnPinning={true}
                    enableGlobalFilter={true}
                    globalFilterFn="contains"
                    displayColumnDefOptions={{
                        "mrt-row-actions": {
                            size: 80,
                            grow: false,
                        },
                    }}
                    renderRowActions={({ row }) => (
                        <IconButton
                            onClick={(e) => {
                                setSelectedRowId(row.original.id);
                                setRowMenuAnchor(e.currentTarget);
                            }}
                            sx={{ transform: "translateX(-4px)" }}
                        >
                            <MoreHoriz />
                        </IconButton>
                    )}
                    state={{
                        isLoading,
                        globalFilter: search,
                        pagination: paginationModel,
                        sorting,
                        columnPinning: {
                            left: ["name"],
                            right: ["mrt-row-actions"],
                        },
                    }}
                    onGlobalFilterChange={setSearch}
                    onPaginationChange={setPaginationModel}
                    onSortingChange={setSorting}
                    rowCount={totalItems}
                    manualPagination={true}
                    manualSorting={true}
                    localization={{
                        actions: "",
                    }}
                />
            </Box>

            {/* Menu contextuel */}
            <ActionsPopover
                menuItems={menuItems}
                anchor={rowMenuAnchor}
                closeWindow={() => {
                    setRowMenuAnchor(null);
                    setSelectedRowId(null);
                }}
            />

            {/* Dialog de confirmation de suppression */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, tagType: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t("common.actions.confirmDelete", "Confirmer la suppression")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(
                            "settings.tags.tagTypes.confirmDelete",
                            'Are you sure you want to delete the tag type "{{name}}"? This action is irreversible.',
                            { name: deleteDialog.tagType?.name },
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, tagType: null })} color="primary">
                        {t("common.actions.cancel", "Annuler")}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteTagTypeMutation.isLoading}
                    >
                        {t("common.actions.delete", "Supprimer")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Second-step modal opened only when the backend returned a 409
                with a non-zero values + usage count. Lets the user back out or
                proceed anyway, in which case the backend strips the references
                and cascade-deletes child values first. */}
            <Dialog
                open={inUseDialog.open}
                onClose={() => setInUseDialog({ open: false, tagType: null, valuesCount: 0, usageCount: 0 })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t("settings.tags.tagTypes.inUseDialog.title", "This tag type is in use")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {inUseDialog.valuesCount > 0 && inUseDialog.usageCount > 0 ?
                            t(
                                "settings.tags.tagTypes.inUseDialog.bodyValuesAndUsage",
                                'The tag type "{{name}}" has {{valuesCount}} value(s) and is currently used by {{usageCount}} entity(ies). Deleting it will remove every value and strip the references from those entities. Do you want to continue?',
                                {
                                    name: inUseDialog.tagType?.name,
                                    valuesCount: inUseDialog.valuesCount,
                                    usageCount: inUseDialog.usageCount,
                                },
                            )
                        : inUseDialog.valuesCount > 0 ?
                            t(
                                "settings.tags.tagTypes.inUseDialog.bodyValuesOnly",
                                'The tag type "{{name}}" has {{valuesCount}} value(s). Deleting it will remove every value. Do you want to continue?',
                                { name: inUseDialog.tagType?.name, valuesCount: inUseDialog.valuesCount },
                            )
                        : inUseDialog.usageCount > 0 ?
                            t(
                                "settings.tags.tagTypes.inUseDialog.bodyUsageOnly",
                                'The tag type "{{name}}" is currently used by {{usageCount}} entity(ies). Deleting it will strip the references from those entities. Do you want to continue?',
                                { name: inUseDialog.tagType?.name, usageCount: inUseDialog.usageCount },
                            )
                        :   t(
                                "settings.tags.tagTypes.inUseDialog.bodyUnknownCount",
                                'The tag type "{{name}}" is currently in use. Deleting it will remove every associated value and strip the references from those entities. Do you want to continue?',
                                { name: inUseDialog.tagType?.name },
                            )
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setInUseDialog({ open: false, tagType: null, valuesCount: 0, usageCount: 0 })}
                        color="primary"
                    >
                        {t("common.actions.cancel", "Annuler")}
                    </Button>
                    <Button
                        onClick={handleForceDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteTagTypeMutation.isLoading}
                    >
                        {t("settings.tags.tagTypes.inUseDialog.confirm", "Delete anyway")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};
